'use client';

import { Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';
import { Dialog, OverlayArrow, Popover as RAC_Popover } from 'react-aria-components';
import { Button } from '~/components/Button';
import { mergeProps } from '~/components/ReactAriaCarousel/utils';
import styles from './Popover.module.css';
import type { PopoverProps } from './Popover.types';

const Popover = forwardRef<HTMLElement, PopoverProps>(
  (
    {
      children,
      variant = 'default',
      hasArrow,
      placement,
      ariaLabel,
      maxWidth,
      close,
      noHeader = false,
      style: incomingStyle,
      onOpenChange,
      border = true,
      title,
      minimalPadding = false,
      freeHeight = false,
      ...props
    },
    ref
  ) => {
    const t = useTranslations('aria');
    const maxWidthStyle = {
      '--popover-max-width': typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth
    } as React.CSSProperties;

    const mergedStyle = mergeProps(incomingStyle || {}, maxWidthStyle);

    // Create the close button component
    const closeButton = (
      <Button variant="icon" ariaLabel={t('close')} onPress={() => onOpenChange?.(false)}>
        <Xmark decorative size="large" />
      </Button>
    );

    const _closeFunction = () => onOpenChange?.(false);

    return (
      <RAC_Popover
        data-test-id="Popover"
        className={clsx(styles.popover, styles[variant], {
          [styles.border]: border === false,
          [styles.minimalPadding]: minimalPadding,
          [styles.freeHeight]: freeHeight
        })}
        style={mergedStyle}
        placement={placement}
        ref={ref}
        onOpenChange={onOpenChange}
        {...props}
      >
        {hasArrow && (
          <OverlayArrow className={styles.overlayArrow}>
            <svg width={12} height={12} viewBox="0 0 8 8" aria-hidden="true">
              <path d="M0 0 L4 4 L8 0" />
            </svg>
          </OverlayArrow>
        )}
        <Dialog aria-label={ariaLabel} className={styles.dialog}>
          {title && title}
          {close && (
            <span
              className={clsx(styles.closeButton, {
                [styles.closeButtonAbsolute]: noHeader
              })}
            >
              {closeButton}
            </span>
          )}
          {children}
        </Dialog>
      </RAC_Popover>
    );
  }
);

Popover.displayName = 'Popover';

export { Popover };
